"use client";

import { c as _c } from "react/compiler-runtime";
import { COMMUNITY_CURATIONS_LIMIT } from "@/components/community-curations/communityCurations.constants";
import CommunityCurationsMasonry from "@/components/community-curations/CommunityCurationsMasonry";
import { useLayout } from "@/components/brain/my-stream/layout/LayoutContext";
import { useCommunityCurationsDrops } from "@/hooks/useCommunityCurationsDrops";
import { useCallback, useState } from "react";
const COMMUNITY_CURATIONS_SKELETON_COLUMNS = [{
  id: "left",
  className: "tw-flex",
  cards: [{
    id: "compact",
    mediaHeight: 210,
    lines: 2
  }, {
    id: "feature",
    mediaHeight: 390,
    lines: 2
  }, {
    id: "portrait",
    mediaHeight: 350,
    lines: 3
  }]
}, {
  id: "middle",
  className: "tw-hidden md:tw-flex",
  cards: [{
    id: "tall",
    mediaHeight: 320,
    lines: 4
  }, {
    id: "balanced",
    mediaHeight: 280,
    lines: 4
  }, {
    id: "wide",
    mediaHeight: 260,
    lines: 2
  }]
}, {
  id: "right",
  className: "tw-hidden xl:tw-flex",
  cards: [{
    id: "mid",
    mediaHeight: 250,
    lines: 3
  }, {
    id: "short",
    mediaHeight: 220,
    lines: 3
  }]
}] as const;
const COMMUNITY_CURATIONS_SKELETON_LINE_IDS = ["headline", "summary", "detail", "caption"] as const;
function CommunityCurationsSkeletonCard(t0) {
  const $ = _c(15);
  const {
    lines,
    mediaHeight
  } = t0;
  let t1;
  if ($[0] !== mediaHeight) {
    t1 = <div className="tw-animate-pulse tw-bg-iron-900" style={{
      height: mediaHeight
    }} />;
    $[0] = mediaHeight;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-size-8 tw-animate-pulse tw-rounded-full tw-bg-iron-800" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="tw-flex tw-items-center tw-gap-3">{t2}<div className="tw-flex-1 tw-space-y-2"><div className="tw-h-3 tw-w-28 tw-animate-pulse tw-rounded tw-bg-iron-800" /><div className="tw-h-3 tw-w-40 tw-animate-pulse tw-rounded tw-bg-iron-900" /></div></div>;
    t4 = <div className="tw-mt-5 tw-h-5 tw-w-3/4 tw-animate-pulse tw-rounded tw-bg-iron-800" />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== lines) {
    t5 = COMMUNITY_CURATIONS_SKELETON_LINE_IDS.slice(0, lines).map((lineId, index) => <div key={`community-curations-skeleton-line-${lineId}`} className={`tw-h-3 tw-animate-pulse tw-rounded tw-bg-iron-900 ${index === lines - 1 ? "tw-w-2/3" : "tw-w-full"}`} />);
    $[5] = lines;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <div className="tw-mt-3 tw-space-y-2">{t5}</div>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="tw-mt-5 tw-flex tw-items-center tw-justify-between tw-gap-3"><div className="tw-flex tw-items-center tw-gap-2"><div className="tw-h-9 tw-w-20 tw-animate-pulse tw-rounded-lg tw-bg-iron-900" /><div className="tw-h-9 tw-w-14 tw-animate-pulse tw-rounded-lg tw-bg-iron-900" /><div className="tw-size-9 tw-animate-pulse tw-rounded-lg tw-border tw-border-dashed tw-border-iron-700/80 tw-bg-iron-900/30" /></div><div className="tw-size-9 tw-animate-pulse tw-rounded-lg tw-bg-iron-900/60" /></div>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6) {
    t8 = <div className="tw-p-4">{t3}{t4}{t6}{t7}</div>;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t1 || $[13] !== t8) {
    t9 = <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950/75">{t1}{t8}</div>;
    $[12] = t1;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
function CommunityCurationsSkeletonGrid() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-3">{COMMUNITY_CURATIONS_SKELETON_COLUMNS.map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(column) {
  return <div key={`community-curations-skeleton-column-${column.id}`} className={`${column.className} tw-flex-col tw-gap-4`}>{column.cards.map(card => <CommunityCurationsSkeletonCard key={`community-curations-skeleton-${column.id}-${card.id}`} lines={card.lines} mediaHeight={card.mediaHeight} />)}</div>;
}
function CommunityCurationsEmptyState(t0) {
  const $ = _c(7);
  const {
    title,
    description
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <p className="tw-mb-2 tw-text-base tw-font-semibold tw-text-iron-100">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = <p className="tw-mb-0 tw-text-sm tw-text-iron-400">{description}</p>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-iron-700 tw-bg-iron-950/70 tw-px-6 tw-py-10 tw-text-center">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export default function CommunityCurations() {
  const $ = _c(26);
  const {
    waveViewStyle
  } = useLayout();
  const [scrollContainer, setScrollContainer] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      limit: COMMUNITY_CURATIONS_LIMIT
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    allDrops,
    drops,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading
  } = useCommunityCurationsDrops(t0);
  const isInitialLoading = isLoading && allDrops.length === 0;
  const hasMorePages = Boolean(hasNextPage) || isFetchingNextPage;
  const shouldShowEmptyState = !isInitialLoading && !isError && drops.length === 0 && !hasMorePages;
  const shouldShowMasonry = !isInitialLoading && !isError && (drops.length > 0 || hasMorePages);
  let t1;
  if ($[1] !== fetchNextPage) {
    t1 = async () => {
      await fetchNextPage();
    };
    $[1] = fetchNextPage;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleFetchNextPage = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-flex tw-flex-col tw-gap-4"><div className="tw-max-w-2xl"><h1 className="tw-mb-0 tw-text-lg tw-font-bold tw-text-white md:tw-text-xl">Latest From Profile Waves</h1><p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-iron-400">Drops 6529 users are featuring from their own profile waves.</p></div></div>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isInitialLoading) {
    t3 = isInitialLoading && <CommunityCurationsSkeletonGrid />;
    $[4] = isInitialLoading;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isError || $[7] !== isInitialLoading) {
    t4 = !isInitialLoading && isError && <CommunityCurationsEmptyState title="Could not load curations" description="Try refreshing this view in a moment." />;
    $[6] = isError;
    $[7] = isInitialLoading;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== shouldShowEmptyState) {
    t5 = shouldShowEmptyState && <CommunityCurationsEmptyState title="No profile wave drops yet" description="Drops will appear here when users feature them from their profile waves." />;
    $[9] = shouldShowEmptyState;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== drops || $[12] !== handleFetchNextPage || $[13] !== hasNextPage || $[14] !== isFetchingNextPage || $[15] !== scrollContainer || $[16] !== shouldShowMasonry) {
    t6 = shouldShowMasonry && <CommunityCurationsMasonry drops={drops} fetchNextPage={handleFetchNextPage} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} scrollContainer={scrollContainer} />;
    $[11] = drops;
    $[12] = handleFetchNextPage;
    $[13] = hasNextPage;
    $[14] = isFetchingNextPage;
    $[15] = scrollContainer;
    $[16] = shouldShowMasonry;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6) {
    t7 = <div className="tw-mx-auto tw-w-full tw-max-w-6xl">{t2}<div className="tw-mt-6">{t3}{t4}{t5}{t6}</div></div>;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t7 || $[24] !== waveViewStyle) {
    t8 = <section ref={setScrollContainer} className="tw-flex tw-min-h-0 tw-w-full tw-flex-grow tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden tw-overscroll-contain tw-px-4 tw-py-8 tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-600 desktop-hover:hover:tw-scrollbar-thumb-iron-500 sm:tw-px-6 lg:tw-px-8" style={waveViewStyle}>{t7}</section>;
    $[23] = t7;
    $[24] = waveViewStyle;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}